// CatalogImportPage — /parts-catalog/import. A purchasing document in, catalog
// rows out.
//
// ── THE ERRAND ───────────────────────────────────────────────────────────────
// Mannie: "in the catalog, I want to make a 'PO/Quote to inventory' scan...
// I want to reuse this logic for ONLY catalog population. to automate catalog
// inputting. I think it will be a huge help."
//
// It is a huge help for one arithmetic reason: the catalog is 32 rows and every
// one was typed, while a real distributor quote is 133 lines.
//
// ── THE ONE THING THAT MAKES OR BREAKS THIS SCREEN ──────────────────────────
// Both classification axes are NOT NULL with no default (migration 016), and that
// is right — a default is a classification nobody made. But 133 lines × 2
// questions is 266 answers, which is not a screen anybody finishes.
//
// So the axes are set ONCE FOR THE DOCUMENT and overridden per line. A quote is
// almost always one kind of thing (a reel of resistors, a box of connectors), so
// the common case is two answers plus a handful of exceptions. Per-line
// suggestions still run first, from the same skuGrammar the other lanes use, and
// the bulk setter only fills what is still blank unless you ask it to overwrite.
//
// Without that, this feature is a worse way to type.
//
// ── THREE GROUPS, NOT ONE LIST ──────────────────────────────────────────────
// New items, rows to fill in, and rows we already have complete. They ask
// genuinely different things of the reader — name a thing, tick a checkbox, and
// nothing at all — and mixing them means reading every row to find out which.
//
// The middle group is the one people will not expect and the one worth the most:
// Firefly's 32 rows have `manufacturer` empty on every single one.
//
// ── A NEW ROW ASKS FOR A KIND, NOT A CODE ───────────────────────────────────
// Every mint row used to open with a SKU box carrying a guess: FF-GMT-02-1 for
// a bag of grommets. That is the catalog's old shape, where inventing a code WAS
// creating an item, and it is the shape docs/features/item-identity.md retired.
// The kind is the question now, and the answer decides what else the row asks:
//
//   short kind      a NAME, and the server assigns GMT-7K3Q on confirm
//   structured kind a SKU, because a board's code is composed from decisions
//                   (revision, assembler, variant) that only a person can make
//
// The kind is guessed from the same stem the SKU suggestion would have used, so
// a document full of grommets and washers usually arrives already answered. A
// stem no kind wears leaves the row blank rather than inventing a taxonomy, and
// Save says so.

import React from 'react';
import { buttonClass } from '../components/ui/Button';
import { Link, Redirect } from 'react-router-dom';
import BackLink from '../components/BackLink';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import HelpDrawer from '../components/HelpDrawer';
import useUserStore from '../stores/useUserStore';
import ScanDraftsPanel, { ScanDraftPages } from '../Procurement/PoScanDrafts';
import { ReviewNote, type ReviewSlot } from '../components/review/ReviewNote';
import { ConfidenceDot } from '../components/review/confidence';
import { laneLabel as labelClass } from '../components/review/fieldStyles';
import { Alert } from '../components/ui/Alert';
import { Checkbox } from '../components/ui/Checkbox';
import EmptyState from '../components/ui/EmptyState';
import FadeIn from '../components/ui/FadeIn';
import Reveal from '../components/ui/Reveal';
import { ListRow, ListStack } from '../components/ui/ListRow';
import Panel from '../components/ui/Panel';
import { Field } from '../components/ui/Field';
import FileField from '../components/ui/FileField';
import { Select } from '../components/ui/Select';
import { Tooltip } from '../components/ui/Tooltip';
import { groupSlots } from '../components/review/groupSlots';
import { ConfirmBar } from '../components/review/ConfirmBar';
import ClassificationGuideButton from './ClassificationGuide';
import {
  MATERIAL_TYPES,
  MATERIAL_TYPE_META,
  COMMODITY_CLASSES,
  COMMODITY_CLASS_META,
} from './classificationDisplay';
import {
  suggestItemSku,
  suggestCommodityClass,
  suggestTypeToken,
  skuLooksMintable,
  parseBoardSku,
  brandFor,
} from './skuGrammar';
import KindPicker from './KindPicker';
import PageLayout from '../components/ui/PageLayout';
import useItemKindsStore, { kindByKey, type CodeMode, type ItemKind } from '../stores/useItemKindsStore';
import type { MaterialType, CommodityClass } from '../Receiving/types';
import {
  useCatalogImportApi,
  reviewKey,
  catalogDraftPageUrl,
  type CatalogImportParseResponse,
  type CatalogImportExtraction,
  type CatalogReviewEntry,
  type CatalogConfirmLineInput,
  type CatalogDraftSummary,
  type ScanDraftFile,
  type CatalogFill,
  type CatalogLineAction,
} from './catalogImportApi';

const REASON_LABELS: Record<string, string> = {
  low_confidence: "AI wasn't sure",
  unverified_text: 'Not found on the page',
  ungrounded_scan: 'Nothing could be checked',
  ai_remark: 'The reader left a note',
  model_not_sure: "AI wasn't sure",
  duplicate_row: 'Listed twice',
  lead_time_implausible: 'Lead time looks wrong',
  conflicts_with_catalog: 'Disagrees with the catalog',
};

const FIELD_LABELS: Record<string, string> = {
  manufacturer: 'Manufacturer',
  mpn: 'Manufacturer part number',
  lead_time_days: 'Lead time',
};

/** A lead time in the words somebody would use, not in days. */
function leadLabel(days: number | null, printed: string | null): string {
  if (days === null || days === undefined) return '—';
  if (days === 0) return printed ? `in stock (${printed})` : 'in stock';
  const weeks = days / 7;
  const rounded = Number.isInteger(weeks) ? `${weeks} week${weeks === 1 ? '' : 's'}` : `${days} days`;
  return printed && printed.toLowerCase() !== rounded.toLowerCase() ? `${rounded} (${printed})` : rounded;
}

interface LineDraft {
  key: string;
  lineNumber: number;
  action: CatalogLineAction;
  description: string;
  partNumber: string | null;
  manufacturer: string;
  leadTimeDays: number | null;
  leadTimeText: string | null;
  included: boolean;
  // mint
  /** `item_kinds.key`. '' means nobody has said what this is yet. */
  kindKey: string;
  /** What people will call it. Required for a short kind, which has no other
   *  identity; a board's name is generated from its SKU, so it is not asked for. */
  name: string;
  /** Only ever sent for a structured kind. Still suggested for every mint row,
   *  so switching a row to a board kind finds the field already filled. */
  sku: string;
  materialType: MaterialType | '';
  commodityClass: CommodityClass | '';
  mpn: string;
  // fill
  partCatalogId: number | null;
  matchedSku: string | null;
  fills: CatalogFill[];
  existing: CatalogImportLineExisting | null;
}

type CatalogImportLineExisting = NonNullable<
  CatalogImportParseResponse['extraction']['lines'][number]['existing']
>;

/** parts_catalog.name is varchar(60); the backend refuses anything longer. */
const NAME_MAX = 60;

/**
 * `structured`, `short`, or null when the row has no kind or the taxonomy has
 * not arrived. Null is what every "this row is not answered yet" branch reads.
 */
function codeModeOf(kinds: ItemKind[], key: string): CodeMode | null {
  if (!key) return null;
  return kindByKey(kinds, key)?.code_mode ?? null;
}

/** Does this row's one error belong to the code field? The ring goes on the
 *  input that can fix it, and the classification errors cannot. */
function isCodeError(err: string): boolean {
  return err.includes('SKU') || err.includes('already creating') || err.includes('the kind is');
}

/**
 * The same SKU with the kind in its second segment.
 *
 * A board's code CARRIES its kind (`FF-PYR-02-1-CH` is a PYR), and mint refuses
 * a kind_key that disagrees with the segment. So a row that opened as a WIDG and
 * was then called a PCB has to re-stem, or Save trades one blocker on this
 * screen for a refusal from the server. Anything already stemmed correctly, and
 * anything too short to have a second segment, is left exactly as typed.
 */
function withKindSegment(sku: string, key: string): string {
  const parts = sku.split('-');
  if (parts.length < 2 || parts[1] === key) return sku;
  parts[1] = key;
  return parts.join('-');
}

function draftLinesFrom(staged: CatalogImportParseResponse, kinds: ItemKind[]): LineDraft[] {
  const brand = brandFor(staged.instance);
  const claimed = new Set<string>();
  return staged.extraction.lines.map((line, index) => {
    const description = line.description || line.part_number || '';
    let sku = '';
    let kindKey = '';
    if (line.action === 'mint') {
      sku = suggestItemSku({ description, brand, tailMax: staged.sku_tail_max, alsoTaken: claimed });
      claimed.add(sku);
      // The same token the SKU suggestion puts in its second segment, and the
      // same guess the scan and quote cards make ("grommets" -> GMT). Where a
      // live kind wears that key, the grammar has already answered the kind
      // question and the row opens filled in. Where none does, the token was a
      // code fragment and nothing more, so the row opens blank rather than
      // proposing a kind nobody defined.
      const guess = kindByKey(kinds, suggestTypeToken(description));
      if (guess && guess.is_active) kindKey = guess.key;
    }
    return {
      key: `l${index}`,
      lineNumber: line.line_number,
      action: line.action,
      description,
      partNumber: line.part_number,
      manufacturer: line.manufacturer || '',
      leadTimeDays: line.lead_time_days,
      leadTimeText: line.lead_time_text,
      included: line.included !== false,
      kindKey,
      // The line's own words are the best name anybody has, and the person is
      // reading them anyway one column across. Truncated rather than refused:
      // a distributor's line can run long, and the tail is packaging detail.
      name: line.action === 'mint' ? description.trim().slice(0, NAME_MAX) : '',
      sku,
      // The SAP-honest answer for anything on a purchasing document, and the one
      // preselection here defensible from the fact of having been bought.
      materialType: line.action === 'mint' ? 'raw_material' : '',
      // Preselected ONLY when the line's own words justify it. Otherwise blank,
      // and Save names the line that is still empty.
      commodityClass: line.action === 'mint' ? suggestCommodityClass(description) : '',
      mpn: line.part_number || '',
      partCatalogId: line.part_catalog_id,
      matchedSku: line.matched_sku,
      // Every fill starts APPLIED. Deferring is the exception and takes one
      // click; making somebody tick thirty boxes to get the ordinary outcome is
      // how a screen stops being worth opening.
      fills: (line.fillable || []).map((f) => ({ ...f, apply: true })),
      existing: line.existing,
    };
  });
}

export const CatalogImportPage: React.FC = () => {
  const api = useCatalogImportApi();
  const rawApi = useApi();
  const toast = useToast();
  const user = useUserStore((s) => s.user);
  // Subscribed, not read through a selector method: the store's helpers return a
  // fresh array every call and would re-render this page forever.
  const kinds = useItemKindsStore((s) => s.kinds);
  const loadKinds = useItemKindsStore((s) => s.load);

  const canAccess = user.isadmin || user.can_admin || user.can_procurement;

  const [parsed, setParsed] = React.useState<CatalogImportParseResponse | null>(null);
  const [lines, setLines] = React.useState<LineDraft[]>([]);
  const [reading, setReading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [readError, setReadError] = React.useState<string | null>(null);
  const [resolved, setResolved] = React.useState<Record<string, 'edited' | 'confirmed_as_staged'>>({});

  const [drafts, setDrafts] = React.useState<CatalogDraftSummary[]>([]);
  const [draftId, setDraftId] = React.useState<number | null>(null);
  const [draftFiles, setDraftFiles] = React.useState<ScanDraftFile[]>([]);
  const [draftBusyId, setDraftBusyId] = React.useState<number | null>(null);

  const fileInput = React.useRef<HTMLInputElement | null>(null);

  const extraction: CatalogImportExtraction | null = parsed ? parsed.extraction : null;

  // The kit's grouping, shared with the quote and packing-list lanes: one amber
  // note per place on the page, one "Looks right" clearing all of it.
  const slots = React.useMemo(
    () =>
      groupSlots(parsed?.review_required || [], {
        // A RING, not a border: the cards it lands on carry no outline to
        // recolor, and a ring adds no layout so a flagged card stays put.
        ringClass: ' ring-2 ring-amber-500/60',
        isResolved: (key) => Boolean(resolved[key]),
        resolve: (key, resolution) => setResolved((r) => ({ ...r, [key]: resolution })),
      }),
    [parsed, resolved]
  );

  const slotFor = (entry: Pick<CatalogReviewEntry, 'target' | 'field' | 'line_number'>): ReviewSlot | null =>
    slots.get(reviewKey(entry)) || null;

  const openSlots = [...slots.values()].filter((s) => s.open);
  const documentSlot = slotFor({ target: 'document', field: null, line_number: null });
  // Every slot must have somewhere to be cleared or the gate deadlocks. Field
  // targets have no natural home on this layout, so they render as one block.
  const fieldSlots = [...slots.values()].filter(
    (slot) => slot.entries.length > 0 && slot.entries[0].target === 'field'
  );

  const mintLines = lines.filter((l) => l.action === 'mint');
  const fillLines = lines.filter((l) => l.action === 'fill');
  const completeLines = lines.filter((l) => l.action === 'complete');

  // ── what still blocks Save ───────────────────────────────────────────────
  const lineErrors = React.useMemo(() => {
    const out = new Map<string, string>();
    const seenSku = new Map<string, number>();
    for (const line of mintLines) {
      if (!line.included) continue;
      const mode = codeModeOf(kinds, line.kindKey);
      // First, because it decides which of the next two questions the row asks.
      if (!mode) { out.set(line.key, 'Say what kind of thing this is.'); continue; }
      if (mode === 'short') {
        // No SKU to be unique against: the server assigns one, and two bags of
        // grommets bought a year apart are allowed to share a name.
        if (!line.name.trim()) { out.set(line.key, 'This new item needs a name.'); continue; }
      } else {
        const sku = line.sku.trim().toUpperCase();
        if (!sku) { out.set(line.key, 'This new item needs a SKU.'); continue; }
        if (!skuLooksMintable(sku)) {
          out.set(line.key, 'A SKU starts with FF- or SD- and is 50 characters or fewer.');
          continue;
        }
        // A BOARD CODE CARRIES ITS KIND, and mint refuses the two disagreeing.
        // Re-stemming on a kind change does not cover this: the field is free
        // text, so somebody who picks PYR and then types an AP code has built a
        // card that only fails at the server, on the one confirm that was meant
        // to create thirty items. A code too loose to parse is left alone, since
        // a non-board SKU may wear any kind.
        const board = parseBoardSku(sku);
        if (board && board.board !== line.kindKey) {
          out.set(
            line.key,
            `Line ${line.lineNumber}: the code says ${board.board}, but the kind is ` +
            `${line.kindKey}. Change one so they agree.`
          );
          continue;
        }
        if (seenSku.has(sku)) {
          out.set(line.key, `Line ${seenSku.get(sku)} is already creating ${sku}.`);
          continue;
        }
        seenSku.set(sku, line.lineNumber);
      }
      if (!line.materialType) { out.set(line.key, 'Pick a build stage.'); continue; }
      if (!line.commodityClass) out.set(line.key, 'Pick an item type.');
    }
    return out;
  }, [mintLines, kinds]);

  const includedCount = lines.filter((l) => l.included).length;
  const appliedFillCount = fillLines
    .filter((l) => l.included)
    .reduce((n, l) => n + l.fills.filter((f) => f.apply).length, 0);

  const blockers: string[] = [];
  if (includedCount === 0) blockers.push('Keep at least one line.');
  // A document of nothing but fills, every box unticked, passed every check and
  // the server answered `nothing_to_do` as a red toast. The screen already knows.
  if (includedCount > 0 && mintLines.filter((l) => l.included).length === 0 && appliedFillCount === 0) {
    blockers.push('Nothing is switched on to save. Tick a field to fill in, or keep a new item.');
  }
  if (lineErrors.size > 0) {
    blockers.push(
      `${lineErrors.size} new item${lineErrors.size === 1 ? '' : 's'} `
      + `still ${lineErrors.size === 1 ? 'needs' : 'need'} an answer.`,
    );
  }
  if (openSlots.length > 0) {
    blockers.push(`${openSlots.length} thing${openSlots.length === 1 ? '' : 's'} to look at: ${openSlots.map((s) => s.label).join(', ')}.`);
  }

  const refreshDrafts = React.useCallback(async () => {
    try {
      const data = await api.listDrafts();
      setDrafts(data.drafts || []);
    } catch {
      // A convenience on top of a flow that works without it.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => { refreshDrafts(); }, [refreshDrafts]);

  // Before a document can be drafted, not when the first picker mounts: the kind
  // guess runs once at hydrate and cannot be redone behind somebody's edits.
  React.useEffect(() => {
    loadKinds(rawApi);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function hydrate(staged: CatalogImportParseResponse, id: number | null, files: ScanDraftFile[] = []) {
    setParsed(staged);
    // getState, not the subscribed `kinds`: hydrate runs from an async handler,
    // and the taxonomy may have landed after this render's closure was made.
    setLines(draftLinesFrom(staged, useItemKindsStore.getState().kinds));
    // Deliberately NOT restored: which flags were cleared is a judgment,
    // possibly made in another session by another person.
    setResolved({});
    setDraftId(id);
    setDraftFiles(files);
  }

  function reset() {
    setParsed(null);
    setLines([]);
    setDraftId(null);
    setDraftFiles([]);
    setResolved({});
  }

  async function openDraft(id: number) {
    setDraftBusyId(id);
    setReadError(null);
    try {
      const draft = await api.getDraft(id);
      hydrate(draft.staged_doc, draft.id, draft.files);
    } catch (err: any) {
      setReadError(err?.data?.error?.message || err?.message || 'That document could not be opened.');
      refreshDrafts();
    } finally {
      setDraftBusyId(null);
    }
  }

  async function discardDraft(id: number) {
    setDraftBusyId(id);
    try {
      await api.discardDraft(id);
      if (draftId === id) reset();
      await refreshDrafts();
    } catch (err: any) {
      toast.error(err?.data?.error?.message || err?.message || 'That document could not be discarded.');
    } finally {
      setDraftBusyId(null);
    }
  }

  async function readDocument(files: FileList | null) {
    if (!files || files.length === 0) return;
    setReading(true);
    setReadError(null);
    try {
      const result = await api.parse(Array.from(files));
      hydrate(result, result.draft_id ?? null, []);
      refreshDrafts();
    } catch (err: any) {
      setReadError(err?.data?.error?.message || err?.message || 'That document could not be read.');
      reset();
    } finally {
      setReading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  const patchLine = (key: string, patch: Partial<LineDraft>) =>
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const patchFill = (key: string, field: string, apply: boolean) =>
    setLines((prev) => prev.map((l) => (l.key === key
      ? { ...l, fills: l.fills.map((f) => (f.field === field ? { ...f, apply } : f)) }
      : l)));

  /**
   * Set an axis across every new item at once.
   *
   * IT REPLACES. It used to fill only blanks, on the reasoning that the grammar's
   * per-line guesses were better than one answer for everything — which made
   * "Build stage for all" a DEAD CONTROL, because draftLinesFrom presets every
   * mint line to raw_material and nothing was ever blank. You picked a value, the
   * select reset itself, and nothing changed.
   *
   * Replacing is also the honest reading of the action. Somebody who opens a
   * dropdown labeled "set every one to" has looked at the document and decided;
   * quietly declining to apply it to the rows we had already guessed at is the
   * screen second-guessing a person who knows more than it does.
   */
  function setAxisForAll(axis: 'materialType' | 'commodityClass', value: string) {
    if (!value) return;
    setLines((prev) => prev.map((l) => (l.action === 'mint' ? { ...l, [axis]: value } : l)));
  }

  async function save() {
    if (!parsed || !extraction || blockers.length > 0) return;
    setSaving(true);
    try {
      const payloadLines: CatalogConfirmLineInput[] = lines.map((line) => {
        if (line.action !== 'mint') {
          return {
            action: line.action,
            included: line.included,
            description: line.description || null,
            part_catalog_id: line.partCatalogId,
            fills: line.fills,
          };
        }
        const short = codeModeOf(kinds, line.kindKey) === 'short';
        return {
          action: line.action,
          included: line.included,
          description: line.description || null,
          mint: {
            kind_key: line.kindKey,
            // A short item is named by a person and coded by the server. A board
            // is the other way round: its name is composed from the SKU's own
            // attributes, so sending the distributor's line as its permanent
            // name would overwrite a better answer with an unread one.
            name: short ? line.name.trim() : null,
            ...(short ? {} : { sku: line.sku.trim().toUpperCase() }),
            material_type: line.materialType as MaterialType,
            commodity_class: line.commodityClass as CommodityClass,
            description: line.description || null,
            mpn: line.mpn.trim() || null,
            manufacturer: line.manufacturer.trim() || null,
            lead_time_days: line.leadTimeDays,
          },
        };
      });

      const result = await api.confirm({
        document: {
          source: parsed.source,
          source_filename: parsed.source_filename,
          lines: payloadLines,
        },
        ...(draftId ? { draft_id: draftId } : {}),
        staged_extraction: extraction,
        staged_confidence: parsed.confidence,
        staged_warnings: parsed.warnings,
        // ONLY what was actually answered — never a blanket map of every entry,
        // which would make the server's gate satisfiable by its own client.
        reviews: (parsed.review_required || [])
          .filter((entry) => resolved[reviewKey(entry)])
          .map((entry) => ({
            target: entry.target,
            field: entry.field,
            line_number: entry.line_number,
            resolution: resolved[reviewKey(entry)],
          })),
      });

      const parts = [];
      if (result.minted_count) parts.push(`${result.minted_count} item${result.minted_count === 1 ? '' : 's'} added`);
      if (result.filled_count) parts.push(`${result.filled_count} filled in`);
      toast.success(`${parts.join(', ') || 'Nothing to do'}.`);
      // Somebody else got there first. Said out loud rather than swallowed, so
      // nobody wonders why a box they ticked changed nothing.
      for (const s of result.skipped || []) {
        toast.info(`${s.sku}'s ${FIELD_LABELS[s.field] || s.field} was already "${s.kept}", so it was left alone.`);
      }
      reset();
      refreshDrafts();
    } catch (err: any) {
      toast.error(err?.data?.error?.message || err?.message || 'Those items could not be saved.');
    } finally {
      setSaving(false);
    }
  }

  if (!canAccess) return <Redirect to="/" />;

  return (
    <PageLayout width="wide"
      kind="workspace"
      className="space-y-5 pt-4"
      header={
        <>
      <BackLink to="/parts-catalog" label="Catalog" className="-mb-3" />
      <div className="flex items-center gap-2 flex-wrap">
        <h1 className="text-2xl font-bold text-primary">Catalog items from a document</h1>
        <HelpDrawer title="About cataloging from a document">
          <p>
            Drop a purchase order, a quote, an order acknowledgement or a distributor's
            line list here. It is read once for what the items <span className="text-on-surface">are</span>:
            what each one is, who makes it, their part number, and how long it takes to arrive.
            Then you check it and the catalog fills itself in.
          </p>
          <p className="text-on-surface font-medium">It only touches the catalog</p>
          <p>
            No purchase order is created, nothing is ordered, nothing is received and no
            price is recorded. You can run a three-year-old quote through this purely to
            harvest the part numbers off it.
          </p>
          <p className="text-on-surface font-medium">Three kinds of line</p>
          <p>
            <span className="text-on-surface">New</span> means nothing in the catalog matches
            and it will be created.{' '}
            <span className="text-on-surface">Fill in</span> means we already have the item and
            this document knows something we do not, usually who makes it.{' '}
            <span className="text-on-surface">Already have</span> means there is nothing to add,
            and those are switched off.
          </p>
          <p className="text-on-surface font-medium">Nothing is ever overwritten</p>
          <p>
            A document can only fill in a field that is currently empty. If it disagrees with
            something we already hold, we keep ours and tell you. Change it on the item itself
            if the document is right.
          </p>
          <p className="text-on-surface font-medium">Lead times can be one-offs</p>
          <p>
            A quote for a rush order or a particular quantity can carry a lead time that is
            not the normal one for that part. Untick it and it will not be saved. Who makes a
            part cannot be a one-off, so that one is not flagged.
          </p>
          <p className="text-on-surface font-medium">What kind of thing it is</p>
          <p>
            Every new item starts with its kind, and most rows arrive with it already
            worked out from the wording. The kind decides what else the row asks for:
            for most things you give it a name and the code is assigned when you save,
            and for a circuit board you compose the code yourself.
          </p>
          <p className="text-on-surface font-medium">Build stage and item type</p>
          <p>
            Every new item needs both, and neither is guessed for you. Set them once at the
            top for the whole document, then change any line that is different. A long quote
            is usually all one kind of thing.
          </p>
        </HelpDrawer>
      </div>
      <p className="text-sm text-on-surface-variant">
        Read the items off a purchasing document. Nothing is ordered and nothing is received.
      </p>
        </>
      }
    >

      {/* ── upload ─────────────────────────────────────────────────────────── */}
      <Panel label="The document">
        <div className="space-y-3">
        <label className="block">
          <span className={labelClass}>The document</span>
          <FileField
            ref={fileInput}
            multiple
            accept=".pdf,.jpg,.jpeg,.png,.webp"
            disabled={reading}
            onChange={(e) => readDocument(e.target.files)}
            aria-label="Choose the document to read"
          />
          <span className="mt-1 block text-xs text-on-surface-variant leading-snug">
            A purchase order, a quote, an acknowledgement or a line list. One PDF, or photos
            of the pages in order.
          </span>
        </label>
        <Reveal show={reading}>
          <p className="text-xs text-primary/90" role="status">Reading the items…</p>
        </Reveal>
        <Alert show={!!readError} tone="warning" live="alert">{readError}</Alert>
        </div>
      </Panel>

      <section className="space-y-2">
        <h2 className="text-[11px] uppercase tracking-wider text-on-surface-variant font-semibold">
          Waiting to be cataloged
        </h2>
        <ScanDraftsPanel
          drafts={drafts}
          busyId={draftBusyId}
          onReview={openDraft}
          onDiscard={discardDraft}
          pageUrl={catalogDraftPageUrl}
          copy={{
            untitled: 'Unread document',
            empty: 'Nothing is waiting. A long document you put down half-read shows up here.',
            discardQuestion: 'Throw this document away? The reading and the pages go with it.',
          }}
        />
      </section>

      {parsed && extraction && (
        <FadeIn className="space-y-4">
          {/* ── the summary and the bulk setter ─────────────────────────── */}
          <Panel label="Document summary">
            <div className="space-y-4">
            <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-sm">
              {/* How sure the reader was about this document as a whole. Every
                  document in this lane is an AI reading, so it always shows.
                  self-center: this row aligns on the baseline, where a dot with
                  no text of its own would sit low. */}
              <ConfidenceDot confidence={parsed.confidence} className="self-center" />
              <span className="text-on-surface font-mono">
                {extraction.document_reference || parsed.source_filename || 'Document'}
              </span>
              <span className="text-on-surface-variant tabular-nums">
                {parsed.counts.mint} new · {parsed.counts.fill} to fill in · {parsed.counts.complete} already have
              </span>
              <span className="text-on-surface-variant text-xs tabular-nums ml-auto">
                {extraction.manufacturer_count} of {extraction.line_count} name a manufacturer ·{' '}
                {extraction.lead_time_count} give a lead time
              </span>
            </div>

            {mintLines.length > 0 && (
              <Panel label="Classify all new items" on="panel" padding="sm" className="space-y-2">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-[11px] uppercase tracking-wider text-on-surface-variant font-semibold">
                    Classify all {mintLines.length} new items
                  </span>
                  <ClassificationGuideButton />
                </div>
                <p className="text-[11px] text-on-surface-variant leading-snug">
                  Sets every new item below, replacing what is there. A long document is
                  usually all one kind of thing; change any line that is not.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <label className="block">
                    <span className={labelClass}>Build stage for all</span>
                    <Select
                      label="Set the build stage for every new item"
                      value={null}
                      placeholder="Set every blank one to…"
                      onChange={(key) => { setAxisForAll('materialType', key); }}
                      options={MATERIAL_TYPES.map((t) => ({ key: t, label: MATERIAL_TYPE_META[t].label }))}
                    />
                  </label>
                  <label className="block">
                    <span className={labelClass}>Item type for all</span>
                    <Select
                      label="Set the item type for every new item"
                      value={null}
                      placeholder="Set every blank one to…"
                      onChange={(key) => { setAxisForAll('commodityClass', key); }}
                      options={COMMODITY_CLASSES.map((c) => ({ key: c, label: COMMODITY_CLASS_META[c].label }))}
                    />
                  </label>
                </div>
              </Panel>
            )}

            {draftId !== null && draftFiles.length > 0 && (
              <ScanDraftPages
                draftId={draftId}
                files={draftFiles}
                sourceFilename={parsed.source_filename}
                pageUrl={catalogDraftPageUrl}
                noun="document"
              />
            )}

            <Reveal show={!!documentSlot}>
              {documentSlot && <ReviewNote slot={documentSlot} labels={REASON_LABELS} />}
            </Reveal>
            {fieldSlots.map((slot) => (
              <div key={slot.key}>
                <span className="text-[10px] uppercase tracking-wider text-on-surface-variant">{slot.label}</span>
                <ReviewNote slot={slot} labels={REASON_LABELS} />
              </div>
            ))}
            </div>
          </Panel>

          {/* ── NEW ITEMS ──────────────────────────────────────────────
              A TABLE, not the card stack this used to be. The stack was fine for
              the four-line document I built it against and unusable at the volume
              that justifies the feature: a 133-line quote became 133 cards of four
              stacked selects, about nine screens of scrolling with no way to see
              that column three was blank on row ninety.

              Every other list surface in this app is a table for the same reason
              (PartsCatalogPage, ReceivingHubPage, PurchaseOrderList), and matching
              them means somebody who can read one can read this. */}
          {mintLines.length > 0 && (
            <section className="space-y-2">
              <div className="flex items-baseline justify-between gap-3 flex-wrap">
                <h2 className="text-sm font-semibold text-on-surface">
                  New items
                  <span className="text-on-surface-variant font-normal ml-1.5 tabular-nums">
                    ({mintLines.filter((l) => l.included).length} of {mintLines.length} selected)
                  </span>
                </h2>
                <span className="text-[11px] text-on-surface-variant">
                  Everything here is created. Untick anything you do not want.
                </span>
              </div>

              {/* The shared table's face, hand-built because this one's cells
                  are editable fields rather than values: shell one step up from
                  the page, header band two, row hover one below the fields it
                  holds so a hovered row never swallows its own inputs. */}
              <div className="overflow-hidden rounded-md bg-surface-container-low">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-surface-container-high text-[11px] uppercase tracking-wider text-on-surface-variant">
                        <th className="w-8 px-3 py-2" />
                        <th className="text-left px-3 py-2 font-semibold">Kind</th>
                        {/* One column, two questions, because a kind only ever
                            asks one of them: a short item is identified by its
                            name and a board by its code. */}
                        <th className="text-left px-3 py-2 font-semibold">Name or code</th>
                        <th className="text-left px-3 py-2 font-semibold">Description</th>
                        <th className="text-left px-3 py-2 font-semibold">Manufacturer</th>
                        <th className="text-left px-3 py-2 font-semibold">Mfr part no.</th>
                        <th className="text-left px-3 py-2 font-semibold">Build stage</th>
                        <th className="text-left px-3 py-2 font-semibold">Item type</th>
                        <th className="text-right px-3 py-2 font-semibold">Lead time</th>
                      </tr>
                    </thead>
                    <tbody data-testid="mint-lines">
                      {mintLines.map((line) => {
                        const slot = slotFor({ target: 'line', field: null, line_number: line.lineNumber });
                        const err = lineErrors.get(line.key);
                        const mode = codeModeOf(kinds, line.kindKey);
                        return (
                          <React.Fragment key={line.key}>
                            <tr
                              data-testid="mint-line"
                              className={`border-b border-outline-variant transition-colors duration-150 ease-emphasized last:border-b-0 motion-reduce:transition-none ${
                                line.included ? 'hover:bg-surface-container' : 'opacity-50'
                              }${err ? ' bg-red-900/10' : ''}${slot?.open ? ' bg-amber-500/[0.04]' : ''}`}
                            >
                              <td className="px-3 py-2 align-top">
                                <Checkbox
                                  size="sm"
                                  className="mt-1.5"
                                  checked={line.included}
                                  onChange={(e) => patchLine(line.key, { included: e.target.checked })}
                                  aria-label={`Create line ${line.lineNumber}`}
                                />
                              </td>
                              <td className="px-3 py-2 align-top">
                                {/* The label is the field's accessible name and
                                    the column heading is its visible one, so it
                                    is carried rather than repeated on screen. */}
                                <KindPicker
                                  value={line.kindKey || null}
                                  partNumberHint={line.mpn || line.partNumber || null}
                                  onChange={(kind) =>
                                    patchLine(line.key, {
                                      kindKey: kind.key,
                                      ...(kind.code_mode === 'structured'
                                        ? { sku: withKindSegment(line.sku, kind.key) }
                                        : {}),
                                    })
                                  }
                                  size="sm"
                                  label={`Kind, line ${line.lineNumber}`}
                                  labelClassName="sr-only"
                                  placeholder="What is it?"
                                  className="min-w-[10rem]"
                                />
                              </td>
                              <td className="px-3 py-2 align-top">
                                <FadeIn key={mode ?? 'none'}>
                                {mode === 'short' && (
                                  <>
                                    <Field
                                      size="sm"
                                      className="min-w-[10rem]"
                                      error={err === 'This new item needs a name.'}
                                      value={line.name}
                                      maxLength={NAME_MAX}
                                      onChange={(e) => patchLine(line.key, { name: e.target.value })}
                                      aria-label={`Name, line ${line.lineNumber}`}
                                      placeholder="What people call it"
                                    />
                                    {/* Said, not left to be noticed: the column
                                        beside it holds a code on every other row,
                                        and a blank one reads as a missing answer. */}
                                    <span className="mt-1 block text-[11px] text-on-surface-variant">
                                      Code assigned on confirm
                                    </span>
                                  </>
                                )}
                                {mode === 'structured' && (
                                  <Field
                                    size="sm"
                                    className="min-w-[9rem] font-mono"
                                    error={!!err && isCodeError(err)}
                                    value={line.sku}
                                    onChange={(e) => patchLine(line.key, { sku: e.target.value.toUpperCase() })}
                                    aria-label={`SKU, line ${line.lineNumber}`}
                                  />
                                )}
                                {!mode && <span className="text-xs text-on-surface-variant">Pick a kind first</span>}
                                </FadeIn>
                              </td>
                              <td className="px-3 py-2 align-top max-w-[16rem]">
                                <Tooltip label={line.description || ''} truncated>
                                  <p className="text-on-surface truncate">
                                    {line.description}
                                  </p>
                                </Tooltip>
                              </td>
                              <td className="px-3 py-2 align-top">
                                <Field
                                  size="sm"
                                  className="min-w-[8rem]"
                                  value={line.manufacturer}
                                  onChange={(e) => patchLine(line.key, { manufacturer: e.target.value })}
                                  aria-label={`Manufacturer, line ${line.lineNumber}`}
                                  placeholder="Who makes it"
                                />
                              </td>
                              {/* THE FIELD THAT WAS MISSING. It was read, carried in
                                  state and sent in the payload, and never rendered —
                                  so the pair this whole lane exists to harvest was
                                  half invisible and half uneditable. The MPN is the
                                  one a distributor's document gets WRONG, and the
                                  clearing of it is the control. */}
                              <td className="px-3 py-2 align-top">
                                <Field
                                  size="sm"
                                  className="min-w-[8rem] font-mono"
                                  value={line.mpn}
                                  onChange={(e) => patchLine(line.key, { mpn: e.target.value })}
                                  aria-label={`Manufacturer part number, line ${line.lineNumber}`}
                                  placeholder="Their number"
                                />
                              </td>
                              <td className="px-3 py-2 align-top">
                                <Select
                                  size="sm"
                                  className="min-w-[9rem]"
                                  error={err === 'Pick a build stage.'}
                                  value={line.materialType}
                                  placeholder="Select…"
                                  onChange={(key) => patchLine(line.key, { materialType: key as MaterialType | '' })}
                                  label={`Build stage, line ${line.lineNumber}`}
                                  options={[
                                    { key: '', label: 'Select…' },
                                    ...MATERIAL_TYPES.map((t) => ({ key: t, label: MATERIAL_TYPE_META[t].label })),
                                  ]}
                                />
                              </td>
                              <td className="px-3 py-2 align-top">
                                <Select
                                  size="sm"
                                  className="min-w-[9rem]"
                                  error={err === 'Pick an item type.'}
                                  value={line.commodityClass}
                                  placeholder="Select…"
                                  onChange={(key) => patchLine(line.key, { commodityClass: key as CommodityClass | '' })}
                                  label={`Item type, line ${line.lineNumber}`}
                                  options={[
                                    { key: '', label: 'Select…' },
                                    ...COMMODITY_CLASSES.map((c) => ({ key: c, label: COMMODITY_CLASS_META[c].label })),
                                  ]}
                                />
                              </td>
                              <td className="px-3 py-2 align-top text-right text-on-surface-variant whitespace-nowrap">
                                {leadLabel(line.leadTimeDays, line.leadTimeText)}
                              </td>
                            </tr>
                            {(err || slot) && (
                              <tr className="border-b border-outline-variant">
                                <td />
                                <td colSpan={8} className="px-3 pb-2">
                                  {err && <p className="text-[11px] text-red-300">{err}</p>}
                                  {slot && <ReviewNote slot={slot} labels={REASON_LABELS} />}
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>
          )}

          {/* ── FILL IN ────────────────────────────────────────────────── */}
          {fillLines.length > 0 && (
            <section className="space-y-2">
              <div className="flex items-baseline gap-2 flex-wrap">
                <h2 className="text-[11px] uppercase tracking-wider text-on-surface-variant font-semibold">
                  Fill in what we already have ({fillLines.length})
                </h2>
                <span className="text-[11px] text-on-surface-variant">
                  Only empty fields. Nothing is overwritten.
                </span>
              </div>
              <ListStack
                label="Fill in what we already have"
                spacing="loose"
                items={fillLines}
                getKey={(line) => line.key}
                renderRow={(line) => {
                  const slot = slotFor({ target: 'line', field: null, line_number: line.lineNumber });
                  return (
                    <ListRow className={`${line.included ? '' : 'opacity-60'}${slot?.ring || ''}`}>
                      <div
                        // Tagged because each fill's own checkboxes are `<li>` too,
                        // so a listitem query on the outer list matches both levels.
                        data-testid="fill-line"
                        className="space-y-2 px-0.5 py-0.5"
                      >
                      <div className="flex items-start gap-3">
                        <Checkbox
                          className="mt-1 shrink-0"
                          checked={line.included}
                          onChange={(e) => patchLine(line.key, { included: e.target.checked })}
                          aria-label={`Fill in line ${line.lineNumber}`}
                        />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm">
                            <Link
                              to={`/parts-catalog/${line.partCatalogId}`}
                              className="font-mono text-primary hover:underline"
                            >
                              {line.matchedSku}
                            </Link>
                            <span className="text-on-surface-variant"> · {line.existing?.description}</span>
                          </p>
                          <p className="text-[11px] text-on-surface-variant truncate">
                            Matched from “{line.description}”
                          </p>
                        </div>
                      </div>

                      <ul className="space-y-1.5 pl-7">
                        {line.fills.map((f) => (
                          <li key={f.field} className="flex items-start gap-2">
                            <Checkbox
                              size="sm"
                              className="mt-0.5 shrink-0"
                              checked={f.apply !== false}
                              onChange={(e) => patchFill(line.key, f.field, e.target.checked)}
                              aria-label={`Fill in ${FIELD_LABELS[f.field] || f.field} on ${line.matchedSku}`}
                            />
                            <div className="min-w-0">
                              <p className="text-xs text-on-surface">
                                <span className="text-on-surface-variant">{FIELD_LABELS[f.field] || f.field}:</span>{' '}
                                {f.field === 'lead_time_days'
                                  ? leadLabel(Number(f.value), f.printed)
                                  : String(f.value)}
                              </p>
                              {/* The warning goes HERE and nowhere else. Who makes a
                                  thing cannot be circumstantial; how long it takes can. */}
                              {f.circumstantial && (
                                <p className="text-[11px] text-amber-300/80 leading-snug">
                                  A lead time can be a one-off. If this quote was for a rush or a
                                  particular quantity, untick it.
                                </p>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>

                      {slot && <ReviewNote slot={slot} labels={REASON_LABELS} />}
                      </div>
                    </ListRow>
                  );
                }}
              />
            </section>
          )}

          {/* ── ALREADY HAVE ───────────────────────────────────────────── */}
          {completeLines.length > 0 && (
            <section className="space-y-2">
              <h2 className="text-[11px] uppercase tracking-wider text-on-surface-variant font-semibold">
                Already in the catalog ({completeLines.length})
              </h2>
              {/* Shown, not hidden: "we already have this one" is the answer
                  somebody scanning a 133-line document most wants to see. */}
              <ListStack
                label="Already in the catalog"
                items={completeLines}
                getKey={(line) => line.key}
                renderRow={(line) => (
                  <ListRow>
                    <div className="flex items-baseline gap-3 text-xs">
                      <Link
                        to={`/parts-catalog/${line.partCatalogId}`}
                        className="font-mono text-on-surface-variant hover:text-primary"
                      >
                        {line.matchedSku}
                      </Link>
                      <span className="text-on-surface-variant truncate">{line.description}</span>
                      <span className="ml-auto text-on-surface-variant shrink-0">nothing to add</span>
                    </div>
                  </ListRow>
                )}
              />
            </section>
          )}

          {/* ── save ───────────────────────────────────────────────────── */}
          {/* The counts are read fresh at render because they follow every
              include toggle above, so the explainer stays a page-owned slot. */}
          <ConfirmBar
            mode="static"
            className="rounded-lg bg-surface-container-low px-4 py-4 space-y-3"
            blockers={blockers}
            blockersTestId="catalog-blockers"
            label="Add to the catalog"
            busyLabel="Saving…"
            busy={saving}
            disabled={saving || blockers.length > 0}
            onConfirm={save}
            beforeButton={
              <p className="text-xs text-on-surface-variant leading-relaxed">
                This adds {mintLines.filter((l) => l.included).length} item
                {mintLines.filter((l) => l.included).length === 1 ? '' : 's'} and fills in{' '}
                {appliedFillCount} field{appliedFillCount === 1 ? '' : 's'} on items you already
                have. Nothing is ordered and nothing is received.
              </p>
            }
            secondaryButton={
              <button
                type="button"
                onClick={reset}
                className={buttonClass('tonal')}
              >
                Start over
              </button>
            }
          />
        </FadeIn>
      )}

      {!parsed && !reading && (
        <FadeIn>
          <EmptyState title="Nothing read yet.">
            Any purchasing document with a line table will do. It reads what the items are and
            who makes them, and it never orders or receives anything.
          </EmptyState>
        </FadeIn>
      )}
    </PageLayout>
  );
};

export default CatalogImportPage;
